import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { apiClient, type PendingSignup, type TempDefaults } from '@/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

type Props = {
  target: PendingSignup | null;
  defaults: TempDefaults;
  onClose: () => void;
  onApproved: (target: PendingSignup) => void;
};

function parseLimit(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n > 0 ? n : NaN;
}

export default function ApproveSignupDialog({
  target,
  defaults,
  onClose,
  onApproved
}: Props) {
  const [temporary, setTemporary] = useState(false);
  const [days, setDays] = useState('');
  const [inputTokens, setInputTokens] = useState('');
  const [outputTokens, setOutputTokens] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setTemporary(false);
      setDays(defaults.days != null ? String(defaults.days) : '');
      setInputTokens(defaults.input_tokens != null ? String(defaults.input_tokens) : '');
      setOutputTokens(defaults.output_tokens != null ? String(defaults.output_tokens) : '');
      setBusy(false);
    }
  }, [target, defaults]);

  const submit = async () => {
    if (!target) return;
    let body;
    if (temporary) {
      const dayLimit = parseLimit(days);
      const inputLimit = parseLimit(inputTokens);
      const outputLimit = parseLimit(outputTokens);
      if (Number.isNaN(dayLimit) || Number.isNaN(inputLimit) || Number.isNaN(outputLimit)) {
        toast.error('Limits must be whole numbers greater than zero, or left blank.');
        return;
      }
      body = {
        temporary: true,
        day_limit: dayLimit,
        input_token_limit: inputLimit,
        output_token_limit: outputLimit
      };
    } else {
      body = { temporary: false };
    }
    setBusy(true);
    try {
      await apiClient.approvePendingSignup(target.id, body);
      toast.success(`Approved ${target.email}`);
      onApproved(target);
      onClose();
    } catch (error: any) {
      console.error('Approve failed', error);
      toast.error(error?.message || 'Approve failed');
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Approve signup request</AlertDialogTitle>
          <AlertDialogDescription>
            {target ? (
              <>
                Approve{' '}
                <span className="font-medium text-foreground">{target.email}</span>. They
                receive a password-reset email to set up their account.
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <label className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium">Temporary account</span>
            <Switch checked={temporary} onCheckedChange={setTemporary} />
          </label>
          {temporary && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Leave a field blank to not enforce that limit. The day clock starts at the
                user's first login.
              </p>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Days of access</span>
                <Input
                  type="number"
                  min={1}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  placeholder="e.g. 14"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Input token limit</span>
                <Input
                  type="number"
                  min={1}
                  value={inputTokens}
                  onChange={(e) => setInputTokens(e.target.value)}
                  placeholder="e.g. 3000000"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Output token limit</span>
                <Input
                  type="number"
                  min={1}
                  value={outputTokens}
                  onChange={(e) => setOutputTokens(e.target.value)}
                  placeholder="e.g. 250000"
                />
              </label>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            Approve
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
